import api from '../utils/api';

/**
 * @typedef {Object} LessonProgress
 * @property {string} _id
 * @property {string} user
 * @property {string} lesson
 * @property {string} course
 * @property {string} enrollment
 * @property {number} watchTime
 * @property {number} totalDuration
 * @property {number} progressPercentage
 * @property {boolean} isCompleted
 * @property {string} completedAt
 * @property {string} lastWatchedAt
 * @property {number} lastPosition
 * @property {string} createdAt
 * @property {string} updatedAt
 */

export const analyticsService = {
  // ==================
  // STUDENT ROUTES
  // ==================

  /**
   * Update lesson progress
   * @param {string} lessonId 
   * @param {number} progress 
   * @param {boolean} isCompleted 
   * @returns {Promise<{status: string, progress: LessonProgress}>}
   */
  updateLessonProgress: async (lessonId, progress, isCompleted) => {
    const response = await api.post('/api/analytics/progress/update', { lessonId, progress, isCompleted });
    return response;
  },

  /**
   * Get user's lesson progress
   * @param {string} lessonId 
   * @returns {Promise<{status: string, progress: LessonProgress}>}
   */
  getLessonProgress: async (lessonId) => {
    const response = await api.get(`/api/analytics/progress/lesson/${lessonId}`);
    return response;
  },

  // ==================
  // ADMIN ROUTES
  // ==================

  /**
   * Get dashboard summary (Admin)
   * @param {Object} params 
   * @returns {Promise<{status: string, summary: Object}>}
   */
  getDashboardSummary: async (params = {}) => {
    const response = await api.get('/api/analytics/dashboard/summary', { params });
    return response;
  },

  /**
   * Get platform overview (Admin)
   * @param {Object} params 
   * @returns {Promise<{status: string, overview: Object}>}
   */
  getPlatformOverview: async (params = {}) => {
    const response = await api.get('/api/analytics/insights/overview', { params });
    return response;
  },

  /**
   * Get revenue statistics (Admin)
   * @param {Object} params 
   * @returns {Promise<{status: string, revenue: Object}>}
   */
  getRevenueStatistics: async (params = {}) => {
    const response = await api.get('/api/analytics/insights/revenue', { params });
    return response;
  },

  /**
   * Get monthly enrollments (Admin)
   * @param {Object} params 
   * @returns {Promise<{status: string, enrollments: Object[]}>}
   */
  getMonthlyEnrollments: async (params = {}) => {
    const response = await api.get('/api/analytics/insights/enrollments/monthly', { params });
    return response;
  },

  /**
   * Get monthly watch time (Admin)
   * @param {Object} params 
   * @returns {Promise<{status: string, watchTime: Object[]}>}
   */
  getMonthlyWatchTime: async (params = {}) => {
    const response = await api.get('/api/analytics/insights/watch-time/monthly', { params });
    return response;
  },

  /**
   * Get advanced consumption stats (Admin)
   * @param {Object} params 
   * @returns {Promise<{status: string, consumption: Object}>}
   */
  getAdvancedConsumption: async (params = {}) => {
    const response = await api.get('/api/analytics/insights/consumption/advanced', { params });
    return response;
  },

  /**
   * Get most watched lessons (Admin)
   * @param {Object} params 
   * @returns {Promise<{status: string, lessons: Object[]}>}
   */
  getMostWatchedLessons: async (params = {}) => {
    const response = await api.get('/api/analytics/insights/lessons/most-watched', { params });
    return response;
  },

  /**
   * Get most active students (Admin)
   * @param {Object} params 
   * @returns {Promise<{status: string, students: Object[]}>}
   */
  getMostActiveStudents: async (params = {}) => {
    const response = await api.get('/api/analytics/insights/students/most-active', { params });
    return response;
  },

  /**
   * Get course analytics
   * @param {string} courseId 
   * @param {Object} params 
   * @returns {Promise<{status: string, analytics: Object}>}
   */
  getCourseAnalytics: async (courseId, params = {}) => {
    const response = await api.get(`/api/analytics/insights/courses/${courseId}`, { params });
    return response;
  },
};
